import { createRootRouteWithContext, Link, Outlet } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { SHOW_VERSION_OVERLAY } from '@/config/version';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { RouterContext } from '@/routeContext';
import { requireAuthentication } from '@/router/guards';
import { AppVersionOverlay } from '@/ui/shared/AppVersionOverlay';

export const Route = createRootRouteWithContext<RouterContext>()({
  // Root-level session gate (03-05 §5): one restore per navigation, one place that decides
  // where an unauthenticated user goes. Public onboarding routes are exempted inside the guard.
  beforeLoad: requireAuthentication,
  component: RootLayout,
  notFoundComponent: RootNotFound,
});

/**
 * Last-resort 404 (HS-1396).
 *
 * The screen users actually see lives on `_dashboard/$`, which keeps the navigation rail. This
 * one covers what a splat route cannot: a `notFound()` thrown from inside an already-matched
 * route. Nothing throws one today, so this is a backstop rather than a surface.
 *
 * Deliberately styleless — no CSS module, no `@smartsash/ui` primitives. `__root.tsx` is in the
 * entry graph, so anything imported here lands in the initial payload, and `initial CSS` has
 * roughly 2.7 kB of headroom against a budget that may only be lowered (HS-874). Paying that for
 * a branch nobody reaches would be the wrong trade.
 */
function RootNotFound() {
  const { t } = useTranslation();

  return (
    <main>
      <h1>{t('notFound.title')}</h1>
      <p>{t('notFound.description')}</p>
      <Link to="/dashboard">{t('notFound.backToDashboard')}</Link>
    </main>
  );
}

function RootLayout() {
  // Root-level for the same reason as the version overlay: every route gets a named tab, the
  // login screen included.
  useDocumentTitle();

  return (
    <>
      <Outlet />
      {/* Root-level so the build label is present on every route, login included (HS-1208). */}
      {SHOW_VERSION_OVERLAY && <AppVersionOverlay />}
    </>
  );
}
